import React, { useState, useRef, useCallback, useEffect } from 'react';
import { transcribeAudio } from '../../services/factFinderApi';

const HAS_SPEECH_API = typeof window !== 'undefined' &&
  (!!window.SpeechRecognition || !!window.webkitSpeechRecognition);

function VoiceRecorder({ onTranscript }) {
  const [mode, setMode] = useState(null); // null | 'live' | 'whisper'
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [liveText, setLiveText] = useState('');
  const [error, setError] = useState('');

  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch {}
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try { mediaRecorderRef.current.stop(); } catch {}
      }
    };
  }, []);

  // ---- Web Speech API (Live) ----
  const startLiveRecording = useCallback(() => {
    setError('');
    setLiveText('');
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError('Live transcription not supported in this browser.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    let finalTranscript = '';

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interim += transcript;
        }
      }
      setLiveText(finalTranscript + interim);
    };

    recognition.onerror = (event) => {
      if (event.error !== 'aborted') {
        setError(`Speech recognition error: ${event.error}`);
      }
      setRecording(false);
    };

    recognition.onend = () => {
      setRecording(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setMode('live');
    setRecording(true);
  }, []);

  const stopLiveRecording = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setRecording(false);
    if (liveText.trim()) {
      onTranscript(liveText.trim());
      setLiveText('');
    }
    setMode(null);
  }, [liveText, onTranscript]);

  // ---- Whisper (Record & Send) ----
  const startWhisperRecording = useCallback(async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 1000) {
          setError('Recording too short. Please try again.');
          setMode(null);
          return;
        }
        setProcessing(true);
        try {
          const result = await transcribeAudio(blob);
          if (result.success && result.text) {
            onTranscript(result.text);
          } else {
            setError(result.error || 'Transcription failed.');
          }
        } catch (err) {
          setError(err.message || 'Transcription failed.');
        } finally {
          setProcessing(false);
          setMode(null);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setMode('whisper');
      setRecording(true);
    } catch (err) {
      setError('Microphone access denied. Please allow microphone access.');
    }
  }, [onTranscript]);

  const stopWhisperRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  }, []);

  // ---- Render ----
  if (processing) {
    return (
      <div className="ff-voice-recorder" data-testid="voice-recorder">
        <div className="ff-voice-processing">
          <div className="ff-voice-spinner" />
          <span>Transcribing with Whisper...</span>
        </div>
      </div>
    );
  }

  if (recording && mode === 'live') {
    return (
      <div className="ff-voice-recorder" data-testid="voice-recorder">
        <div className="ff-voice-active">
          <div className="ff-voice-pulse" />
          <span className="ff-voice-status">Listening...</span>
          <button
            className="ff-voice-stop-btn"
            onClick={stopLiveRecording}
            data-testid="stop-recording-btn"
          >
            Stop & Insert
          </button>
        </div>
        {liveText && (
          <div className="ff-voice-preview" data-testid="live-transcript">
            {liveText}
          </div>
        )}
      </div>
    );
  }

  if (recording && mode === 'whisper') {
    return (
      <div className="ff-voice-recorder" data-testid="voice-recorder">
        <div className="ff-voice-active ff-voice-whisper-active">
          <div className="ff-voice-pulse ff-pulse-blue" />
          <span className="ff-voice-status">Recording for Whisper...</span>
          <button
            className="ff-voice-stop-btn"
            onClick={stopWhisperRecording}
            data-testid="stop-whisper-btn"
          >
            Stop & Transcribe
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ff-voice-recorder" data-testid="voice-recorder">
      <div className="ff-voice-buttons">
        {HAS_SPEECH_API && (
          <button
            className="ff-voice-btn ff-voice-live"
            onClick={startLiveRecording}
            data-testid="start-live-btn"
            title="Real-time transcription using your browser"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
            Speak Now
          </button>
        )}
        <button
          className="ff-voice-btn ff-voice-whisper"
          onClick={startWhisperRecording}
          data-testid="start-whisper-btn"
          title="Record audio and transcribe with OpenAI Whisper (more accurate)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
          {HAS_SPEECH_API ? 'Record (Whisper)' : 'Record Voice'}
        </button>
      </div>
      {error && <div className="ff-voice-error">{error}</div>}
    </div>
  );
}

export default VoiceRecorder;
